import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, type Memo } from '../../lib/api'
import { Loading, Alert } from '../../components/ui'
import { MEMO_ACCOUNT_MARKER, PARTY_A, SEAL_APPROVERS } from '../../lib/memoTerms'
import { rocDate } from '../../lib/format'

/**
 * 第二條「合約有效期間」那一句的句型。
 *
 * 年月日的位置寫成 [\s　\d]*：原文是全形空白，但也要吃得下先前在正文裡
 * 手打的數字，才能用表單的值覆蓋掉。逗號前後的空白也放寬，避免有人補了半形空格
 * 就整句比對不到。
 */
const PERIOD_RE =
  /自民國[\s　\d]*年[\s　\d]*月[\s　\d]*日起[，,]\s*至自民國[\s　\d]*年[\s　\d]*月[\s　\d]*日止/

/**
 * 合作備忘錄列印版面
 *
 * 版面照《文案撰寫外包合作備忘錄》原始文件，不套發包單／驗收單那組
 * DocHead + 分節表格：置中標題、立合約人兩行、條款逐條排列（條號齊左、
 * 內文縮排）、匯款帳戶表格、最後甲乙雙方簽署區。全文標楷體。
 *
 * 條款正文原樣輸出，系統只做兩件事：
 *   1. 條號那行齊左，其餘縮排 —— 用 CSS 做，換行時仍能對齊
 *   2. {{匯款帳戶}} 換成帳戶表格（原文該處本來就是表格）
 */
export default function MemoPrint() {
  const { id } = useParams()
  const [m, setM] = useState<Memo | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const docRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api.get<Memo>(`/memos/${id}`).then(setM).catch((e) => setError(e.message))
  }, [id])

  /** 字型與圖片都就緒才動作。標楷體還沒載入就輸出，字寬與換行都會跑掉 */
  const whenReady = () =>
    Promise.all([
      document.fonts?.ready ?? Promise.resolve(),
      ...[...document.images].map((img) =>
        img.complete ? Promise.resolve() : new Promise((r) => { img.onload = img.onerror = r })
      ),
    ])

  const printWhenReady = async () => {
    await whenReady()
    window.print()
  }

  /**
   * 直接下載 PDF 檔（不經過列印對話框）——要先寄給寫手填資料簽名，所以需要一個檔案。
   *
   * 做法是把版面渲染成圖片再包進 PDF。瀏覽器沒有別的方式能純前端產出檔案；
   * 代價是文字變成圖片，不能選取搜尋、檔案也比較大。印出來手寫簽名不受影響。
   *
   * 分頁以「元素」為單位算切點，不是把長圖硬切成等高的頁：
   * 硬切一定會從某一行字的中間斷開，合約看起來會像掃壞的。
   */
  const downloadPdf = async () => {
    const el = docRef.current
    if (!el || !m) return
    setBusy('產生 PDF…')
    try {
      await whenReady()
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ])

      /*
       * 截圖前先把只給螢幕看的裝飾關掉（紙張邊框、分頁輔助線），
       * 不然它們會被畫進 PDF——輸出要跟列印版一致。
       *
       * 量測也要在關掉之後做：邊框佔 1px，會讓比例基準跟實際渲染的畫布差一點，
       * 切片位置就會偏個零點幾公釐，剛好卡在表格上緣這種地方就看得出來。
       */
      el.classList.add('pdf-export')
      let canvas: HTMLCanvasElement
      let cuts: number[]
      let mmToPx: number
      try {
        const PAGE_H_MM = 273                        // A4 297mm 扣掉上下各 12mm 邊界
        mmToPx = el.getBoundingClientRect().width / 210   // 版面寬固定 210mm
        const pageHpx = PAGE_H_MM * mmToPx
        const top = el.getBoundingClientRect().top

        /* 每一頁切在哪：逐一累積可分割的區塊（條文段落、表格、簽署區），
           超過一頁可用高度就退回「上一個區塊的結尾」斷頁，不從行中間切 */
        const blocks = [...el.children].map((c) => {
          const r = c.getBoundingClientRect()
          return { start: r.top - top, end: r.bottom - top }
        })
        cuts = [0]
        let limit = pageHpx
        for (const b of blocks) {
          if (b.end > limit) {
            /* 這個區塊本身就比一整頁高（例如超長段落），只能硬切 */
            const cut = b.start > cuts[cuts.length - 1] ? b.start : limit
            cuts.push(cut)
            limit = cut + pageHpx
          }
        }
        cuts.push(el.scrollHeight)

        canvas = await html2canvas(el, {
          /* 3 倍 ≈ 288dpi。2 倍只有 192dpi，中文小字列印會糊；
             再往上檔案大小成長很快，對 email 夾檔不划算 */
          scale: 3,
          backgroundColor: '#fff',
          logging: false,
          windowWidth: el.scrollWidth,
        })
      } finally {
        el.classList.remove('pdf-export')
      }
      /* 垂直比例直接用高度算，不要從寬度推 */
      const ratio = canvas.height / (cuts[cuts.length - 1])

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' })
      for (let i = 0; i < cuts.length - 1; i++) {
        const sliceTop = Math.round(cuts[i] * ratio)
        const sliceH = Math.round((cuts[i + 1] - cuts[i]) * ratio)
        if (sliceH <= 0) continue
        /* 把這一段裁到獨立畫布再貼上，jsPDF 才不會把整份重複塞進每一頁 */
        const part = document.createElement('canvas')
        part.width = canvas.width
        part.height = sliceH
        part.getContext('2d')!.drawImage(canvas, 0, sliceTop, canvas.width, sliceH, 0, 0, canvas.width, sliceH)
        const hMm = (sliceH / ratio) / mmToPx
        if (i > 0) pdf.addPage()
        /* 第一頁的上邊界由版面自己的 padding 提供；第二頁起是從內容中間切開的，
           不補這 12mm 的話文字會直接貼在紙張最上緣 */
        pdf.addImage(part.toDataURL('image/jpeg', 0.92), 'JPEG', 0, i === 0 ? 0 : 12, 210, hMm)
      }

      const who = m.writer_name ?? m.party_b_title ?? m.id
      pdf.save(`合作備忘錄_${who}.pdf`)
    } catch (e: any) {
      setError(`產生 PDF 失敗：${e?.message || e}`)
    } finally {
      setBusy('')
    }
  }

  if (error) return <Alert kind="error">{error}</Alert>
  if (!m) return <Loading />


  /*
   * 匯款帳戶表格一律留空，不帶系統裡的資料。
   * 這張表印出來是給乙方填的——用途是告訴寫手要準備哪些資料，
   * 先代填就失去意義，也可能印出跟實際存摺不一致的舊資料。
   */
  const accountTable = (
    <table className="acct">
      <tbody>
        <tr>
          <th>戶名</th>
          <td colSpan={3} />
        </tr>
        <tr>
          <th>帳號</th>
          <td colSpan={3} />
        </tr>
        <tr>
          <th>銀行名稱</th>
          <td />
          <th className="mid">分行別</th>
          <td />
        </tr>
        <tr>
          <th>分支機構代號</th>
          <td />
          <td className="mid" colSpan={2}>（銀行代號+分行代號,共 7 碼）</td>
        </tr>
      </tbody>
    </table>
  )

  /**
   * 條號那行不縮排，其餘縮排；空行保留成段落間距。
   * intro=true 時，第一段（開頭的總述句）比照條號齊左，不跟著條文縮排。
   */
  const renderText = (text: string, keyPrefix: string, intro = false) => {
    let seenFirst = false
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return <div key={`${keyPrefix}${i}`} className="blank-line" />
      const t = line.trim()
      if (intro && !seenFirst) {
        seenFirst = true
        return <p key={`${keyPrefix}${i}`} className="clause-intro">{line}</p>
      }
      const isTitle = /^第[一二三四五六七八九十]+條/.test(t)
      // 「１、」「２、」這種列點：換行時第二行要對齊編號後面的文字，不是縮回編號下
      const isItem = /^[０-９0-9一二三四五六七八九十]+[、.]/.test(t)
      const cls = isTitle
        ? 'clause-title'
        : ['clause-body', isItem && 'list-item', t.startsWith('*') && 'accent']
            .filter(Boolean)
            .join(' ')
      return <p key={`${keyPrefix}${i}`} className={cls}>{fillPeriod(line)}</p>
    })
  }

  /*
   * 第二條的合約期限：把表單填的生效日／到期日帶進條文的空格裡，並加粗。
   *
   * 用比對句型而不是加標記（像 {{匯款帳戶}} 那樣）的理由：terms 是逐份快照，
   * 既有的備忘錄裡沒有標記，加標記就得回頭改每一筆資料。比對句型的話，
   * 舊資料一樣立刻生效。
   *
   * 空格允許夾雜數字，所以先前在正文裡手打的日期會被表單的值蓋掉——
   * 兩邊不一致時以表單為準，那才是系統認得、會拿去算到期提醒的那一份。
   */
  const fillPeriod = (line: string): ReactNode => {
    const hit = line.match(PERIOD_RE)
    if (!hit) return line
    const from = rocDate(m.effective_from)
    const to = rocDate(m.effective_to)
    /* 沒填的欄位維持原本的全形空白，留給手寫 */
    const cell = (v: string) => (v ? <strong>{v}</strong> : '　　')
    /* 文字一律包在 {} 裡：JSX 的裸文字遇到換行會被當成一個空格，
       「日起，」和「至自民國」之間就會多出原文沒有的空隙 */
    return (
      <>
        {line.slice(0, hit.index)}
        {'自民國'}{cell(from.y)}{'年'}{cell(from.m)}{'月'}{cell(from.d)}{'日起，'}
        {'至自民國'}{cell(to.y)}{'年'}{cell(to.m)}{'月'}{cell(to.d)}{'日止'}
        {line.slice((hit.index ?? 0) + hit[0].length)}
      </>
    )
  }

  /*
   * 公司合約專用章要不要印上去。
   *
   * 兩個條件都成立才印：合約已生效，而且核准的人在授權名單裡。
   * 草稿、待審核、已終止一律留空格給手蓋——沒生效或已失效的文件不該帶著公司章外流。
   */
  const withSeal = m.status === '已生效' && !!m.reviewed_by && SEAL_APPROVERS.includes(m.reviewed_by)

  const roc = rocDate(m.signed_on)
  const terms = m.terms?.trim() ?? ''
  const [before, after] = terms.includes(MEMO_ACCOUNT_MARKER)
    ? terms.split(MEMO_ACCOUNT_MARKER)
    : [terms, '']

  return (
    <>
      <div className="page-head no-print">
        <h2>合作備忘錄列印預覽</h2>
        <div className="spacer" />
        <div className="btn-row">
          <button className="primary" onClick={printWhenReady} disabled={!!busy}>列印</button>
          <button onClick={downloadPdf} disabled={!!busy}>{busy ? '產生中…' : '下載 PDF'}</button>
          <Link to={`/memos/${m.id}`}><button>回到編輯</button></Link>
          <Link to="/memos"><button>返回清單</button></Link>
        </div>
      </div>

      {m.status !== '已生效' && (
        <div className="no-print">
          <Alert kind="warn">
            這份備忘錄目前為「{m.status}」，尚未生效，列印出來僅供內部確認。
          </Alert>
        </div>
      )}

      <div className="print-doc contract-doc" ref={docRef}>
        {/* logo 與發包單同一張，載不到就自己隱藏，不留破圖 */}
        <img
          className="contract-logo"
          src="/logo.png"
          alt="崴浤科技股份有限公司"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
        {/* 標題分兩行：小字前綴在上、文件名放大在下，與發包單／驗收單同一套排法 */}
        <p className="contract-lead">文案撰寫外包</p>
        <h1 className="contract-title">合作備忘錄</h1>

        {/*
          三欄對齊：標籤／當事人／括號說明各自成欄，
          「（以下簡稱甲方）」與「（以下簡稱乙方）」自然對齊成一直行。
          乙方留白給對方手寫，底線寬度跟著甲方公司名那一欄。
        */}
        <div className="party-grid">
          <span>立合約人 ：</span>
          <span>{PARTY_A.name}</span>
          <span>（以下簡稱甲方）</span>

          <span />
          <span className="fill" />
          <span>（以下簡稱乙方）</span>
        </div>

        {terms ? (
          <>
            {renderText(before.replace(/\n+$/, ''), 'b', true)}
            {terms.includes(MEMO_ACCOUNT_MARKER) && accountTable}
            {renderText(after.replace(/^\n+/, ''), 'a')}
          </>
        ) : (
          <p className="no-print" style={{ color: 'var(--bad)' }}>
            （尚未設定條款正文。請到編輯頁的「條款正文」欄位確認內容後再列印。）
          </p>
        )}

        <p className="party-line" style={{ marginTop: 26 }}>立合約人：</p>

        {/*
          * 甲乙方上下排。每方是「方別 + 一組欄位」，標籤用 .lbl 撐成等寬，
          * 冒號才會排成一直線（合約的標準排法）。
          *
          * 簽名蓋章欄與資料欄位並排在右側，不是另起一行——章蓋下去不會壓到
          * 地址、電話那幾行。
          */}
        <div className="sign-block">
          <div className="sign-row">
            <div className="party-body">
              <span className="party-tag">甲方</span>
              <div className="party-fields">
                <p><span className="lbl">公司名稱</span>：{PARTY_A.name}</p>
                <p><span className="lbl">負責人</span>：{PARTY_A.rep}</p>
                <p><span className="lbl">地址</span>：{PARTY_A.address}</p>
                <p><span className="lbl">電話</span>：{PARTY_A.phone}</p>
                <p><span className="lbl">統一編號</span>：{PARTY_A.taxId}</p>
              </div>
            </div>
            <div className="seal-cell">
              <span className="seal-label">簽名蓋章</span>
              {/* 甲方是我方，經授權管理員核准生效後合約專用章直接印上去，不必每份手蓋 */}
              {withSeal && (
                <img className="seal-img" src="/seal-a.png" alt="崴浤科技股份有限公司合約專用章" />
              )}
            </div>
          </div>
          {/* 乙方整區留空給對方親筆填寫，與原始範本一致，不帶系統資料。
              欄位依寫手類型分兩種：廠商是法人，要公司名稱、負責人、統一編號；
              個人只要名稱與身分證。writer_kind 由 /memos/:id 從 writers.kind
              帶出（個人／廠商／內部發包），內部發包是自家人，比照個人。 */}
          <div className="sign-row">
            <div className="party-body">
              <span className="party-tag">乙方</span>
              <div className="party-fields">
                {m.writer_kind === '廠商' ? (
                  <>
                    <p><span className="lbl">公司名稱</span>：</p>
                    <p><span className="lbl">負責人</span>：</p>
                    <p><span className="lbl">地址</span>：</p>
                    <p><span className="lbl">電話</span>：</p>
                    <p><span className="lbl">統一編號</span>：</p>
                  </>
                ) : (
                  <>
                    <p><span className="lbl">名稱</span>：</p>
                    <p><span className="lbl">地址</span>：</p>
                    <p><span className="lbl">電話</span>：</p>
                    <p><span className="lbl">身份證</span>：</p>
                  </>
                )}
              </div>
            </div>
            <div className="seal-cell">
              <span className="seal-label">簽名蓋章</span>
            </div>
          </div>
        </div>

        {/* 年份用民國紀年——前面就寫著「中華民國」，放西元年是錯的。
            年／月／日各自是固定寬度的欄位，數字長短（115 vs 8）不會推移後面的
            單位字，空白時也留一樣的空間給手寫。間距交給 CSS 的 gap，不用全形空白 */}
        <p className="sign-date">
          <span>中華民國</span>
          <span className="num yr">{roc.y}</span>
          <span>年</span>
          <span className="num">{roc.m}</span>
          <span>月</span>
          <span className="num">{roc.d}</span>
          <span>日</span>
        </p>
      </div>
    </>
  )
}
